import type { CatalogEntry } from '../types';

/**
 * Billing (feature `04`) error codes + catalog.
 *
 * Code shape is `04AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`0400xx`, AA=00) are cross-endpoint conditions:
 *   - `OrgNotFound` / `PlanNotFound` / `LimitNotConfigured` come from
 *     `PlanLimitService`, which is called by both `getSubscription` and
 *     `getSummary` (and by email-accounts). They are operator/data-integrity
 *     errors (the org is always resolvable from the JWT), so they map to 500
 *     with generic prose and the real reason in `logContext`.
 *   - `FeatureNotAvailable` (`040004`) is RETIRED: `FeatureGuard` and the boolean
 *     feature-flag layer were deleted (two-guard access model, Phase 4), so nothing
 *     throws it. The entry stays only so the number is never reused and old logs still
 *     resolve; a plan denial is now `ResourceAccessDenied` (`040005`).
 *   - `ResourceAccessDenied` is thrown by `PlanAccessGuard`, which gates
 *     endpoints by `resource/action` capability per subscriptionStatus
 *     — one code for the condition across every guarded route.
 *   - `SubscriptionPaused` is the view-only gate for a PAUSED subscription. It
 *     is thrown from three places across three modules (ApiKeyGuard,
 *     PlanAccessGuard, bulk-enable chunk), so it is a cross-endpoint
 *     condition, not a billing-endpoint one.
 *
 * Endpoint → `AA` map:
 *   handlePaddleWebhook=01, getSubscription=02, getSummary=03, listPlans=04,
 *   getPortalUrl=05, listInvoices=06, createCheckout=07, customPlan=08,
 *   internal=09, upgrade (preview + PATCH)=10, lifecycle (cancel/pause/resume)=11,
 *   extend-trial=12, discount=13.
 *
 * Webhook codes (`0401xx`) are thrown from the webhook controller (HMAC gate)
 * and from `BillingWebhookProcessor`. The processor runs inside the
 * `api-billing-webhook` queue with NO HTTP caller, so its `AppException` never
 * renders an envelope — it's converted only for consistent catalog-backed
 * logging.
 */
export const BILLING_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    OrgNotFound: '040001',
    PlanNotFound: '040002',
    LimitNotConfigured: '040003',
    /** RETIRED — no thrower remains (see the header). Do NOT reuse `040004`. */
    FeatureNotAvailable: '040004',
    ResourceAccessDenied: '040005',
    /**
     * The org's subscription is PAUSED, so the whole panel is view-only:
     * `PlanAccessGuard` rejects every mutating JWT request that
     * isn't `@AllowWhenPaused`, `ApiKeyGuard` rejects every
     * `/v2` call (reads included), and `PlanAccessGuard` also re-maps its own
     * `ResourceAccessDenied` denial to this code, and the bulk `ENABLE_WARMUP`
     * chunk records it per id.
     *
     * It is the GENERAL gate. `EmailAccounts.Shared.SubscriptionPaused`
     * (`020009`) is the same condition thrown by the connect/reconnect path,
     * kept because it has already reached an environment; both translate to
     * public `subscription_paused`. New call sites throw THIS one.
     *
     * Not `ResourceAccessDenied` (040005): that one's remedy is "upgrade", and
     * the web routes it to the upgrade prompt. A paused org must be told to
     * resume instead.
     */
    SubscriptionPaused: '040006',
    /**
     * The subscription is synced from Saleshandy (`subscriptions.source =
     * SALESHANDY`), so every self-serve billing mutation is refused: a Paddle
     * purchase here would overwrite the synced plan and the next Saleshandy
     * sync would overwrite it back, with a real charge in between.
     */
    ManagedBySaleshandy: '040007',
    /**
     * A paid plan assigned by support (APUS, `source = MANUAL`) with no Paddle
     * subscription behind it, checking out a plan that is not strictly higher
     * priced. Moving up is allowed; moving down goes through support.
     */
    ManagedInternally: '040008',
  },
  /** Paddle webhook receiver (HMAC gate) + BillingWebhookProcessor. AA=01. */
  Webhook: {
    MissingSignature: '040101',
    InvalidSignature: '040102',
    /**
     * RETIRED (phase-1 product-id retirement): zero throwers remain — every
     * webhook path resolves by checkout price id and throws
     * `PlanNotFoundForCheckoutPrice` instead. Do not wire new handlers to this;
     * removed in phase 2. Kept for now so the code number isn't reused.
     */
    PlanNotFoundForProduct: '040103',
    MalformedPayload: '040104',
    PlanNotFoundForCheckoutPrice: '040105',
  },
  /** getPortalUrl=05. */
  CustomerPortal: {
    NoActiveSubscription: '040501',
    PortalUrlFailed: '040502',
  },
  /** createCheckout=07. (A missing plan reuses `Shared.PlanNotFound`.) */
  Checkout: {
    PlanNotPurchasable: '040701',
  },
  /**
   * Custom-plan create/edit (`PlanCatalogService` / `AdminService`). AA=08.
   *
   * `MissingRestriction` guards the invariant that every plan carries a
   * `planRestrictions` row for each of `maxEmailAccounts`,
   * `aggregateDailyWarmup`, `warmupDailyMax` and `replyRateCap`. On create the
   * gaps in the caller's `restrictions` map are auto-filled from the parent
   * plan's rows; a code missing from BOTH is unrecoverable, because a missing
   * row resolves to `null` and `null` means UNLIMITED to every consumer — so the
   * plan would silently ship uncapped. Fail loudly instead.
   */
  CustomPlan: {
    ParentPlanNotFound: '040801',
    MissingRestriction: '040802',
    /**
     * The edit targets a BASE catalog plan (`parentId IS NULL`). These endpoints
     * exist to edit *custom* (parented) plans; letting one through would reprice
     * or re-limit a shared catalog plan for every subscriber on it at once.
     */
    NotACustomPlan: '040803',
    /**
     * A `basePrice` change was requested on a plan that has live subscriptions.
     * Paddle prices are IMMUTABLE, so the DB and Paddle would silently disagree:
     * the UI would show the new price while `paddleCheckoutPriceId` still bills
     * the old one. Repricing means publishing a NEW plan row (the dated
     * `-MM-YY` catalog-version scheme), not editing this one in place.
     */
    PriceLockedBySubscribers: '040804',
    /**
     * A plan with this `name` already exists. `plans.name` still carries a UNIQUE
     * index, so without this pre-check the INSERT raises a raw driver duplicate-key
     * error — an uncatalogued 500 — and on the internal path it does so AFTER the
     * Paddle product + price have been minted, orphaning both.
     */
    NameAlreadyExists: '040805',
  },
  /** Internal token-guarded billing-ops endpoints (InternalTokenGuard). AA=09. */
  Internal: {
    Unauthorized: '040901',
    /**
     * A billable plan (one carrying `paddleCheckoutPriceId`, i.e. it exists in
     * Paddle) was assigned without `paddleCustomerId` / `paddleSubscriptionId`.
     * Without them the org has no Paddle link: the customer portal 400s, invoices
     * are local-only, and — because the subscription then carries no
     * `custom_data.orgId` — `extractOrgIdFromPaddlePayload` resolves every one of
     * its webhooks to org `0` and drops them silently.
     */
    PaddleLinkRequired: '040902',
    /**
     * The supplied `paddleSubscriptionId` is billing a price that is NOT this
     * plan's `paddleCheckoutPriceId`. Accepting it would mislink the org: the
     * next `subscription.updated` resolves the plan from the price Paddle bills,
     * so the assignment would be silently reverted to whichever plan owns that
     * price.
     */
    PaddleSubscriptionPlanMismatch: '040903',
    /**
     * Paddle rejected (or was unreachable for) the `custom_data` stamp. Nothing
     * is written locally — the stamp runs BEFORE the DB write precisely so this
     * is a clean retry rather than a half-linked subscription.
     */
    PaddleLinkFailed: '040904',
    /**
     * The assignment changes the org's PLAN and also carries
     * `restrictionOverrides`. A plan change resets entitlement: it DELETEs the
     * org's `subscriptionRestrictions` / `subscriptionResourceIdentifiers`
     * rows so every value falls back to the new plan's
     * catalog defaults. Writing an override in the same call would contradict
     * that reset, and silently DROPPING it would hand the caller a 200 for a
     * value that never landed. Reject instead: assign the plan, then apply the
     * override through its own endpoint.
     */
    OverridesNotAllowedOnPlanChange: '040905',
  },
  /**
   * Self-serve upgrade + proration preview (P2). AA=10. Covers both
   * `GET /billing/subscription/preview` and `PATCH /billing/subscription`.
   * The four validation conditions are 422 (unprocessable given the current
   * subscription state); `PaymentFailed` is 402 (Paddle rejected the immediate
   * prorated charge as a card/decline/SCA/past-due failure — the UI falls back
   * to the customer portal); `UpgradeFailed` is 502 (a transient/network/5xx
   * Paddle failure — the UI offers an inline retry); `PreviewFailed` is 502 (the
   * upstream Paddle preview call failed — mirrors `PortalUrlFailed`);
   * `AlreadyInProgress` is 409 (a per-org upgrade lock is already held — a
   * concurrent double-submit or a second upgrade within the charge→webhook
   * window).
   */
  Upgrade: {
    DowngradeNotAllowed: '041001',
    NoPaddleSubscription: '041002',
    SubscriptionNotActive: '041003',
    NoChange: '041004',
    PaymentFailed: '041005',
    PreviewFailed: '041006',
    AlreadyInProgress: '041007',
    UpgradeFailed: '041008',
  },
  /**
   * Self-serve subscription lifecycle (cancel/pause/resume/revoke). AA=11. The
   * precondition failures are 422 (unprocessable given the current subscription
   * state): `NoPaddleSubscription` (no paddle-backed subscription to act on),
   * `NotActive` (cancel/pause require ACTIVE), `NotPaused` (resume requires
   * PAUSED), `ChangeAlreadyScheduled` (cancel/pause while a scheduled change is
   * already pending — Paddle allows one at a time; thrown by the local guard
   * AND by mapping Paddle's own conflict rejection, so a fast double-click that
   * races ahead of the webhook mirror gets the same 422), `NoScheduledChange`
   * (revoke-scheduled-change with nothing pending to revoke),
   * `PauseAnchorUnknown` (a FIXED-LENGTH pause was requested — one of PAUSE_DURATION_MONTHS —
   * but the subscription carries neither `currentPeriodEnd` nor `expiresAt`, so
   * there is no pause-effective date to measure the N months from; validated
   * BEFORE the Paddle call so an unanswerable resume date never books a pause.
   * An indefinite pause needs no anchor and never hits this). `RequestFailed`
   * is 502 (a genuine Paddle rejection/failure) — the webhook, not these
   * endpoints, applies the resulting local state.
   */
  Lifecycle: {
    NoPaddleSubscription: '041101',
    NotActive: '041102',
    NotPaused: '041103',
    RequestFailed: '041104',
    ChangeAlreadyScheduled: '041105',
    NoScheduledChange: '041106',
    PauseAnchorUnknown: '041107',
  },
  /**
   * One-time self-serve trial extension (`POST /billing/subscription/extend-trial`).
   * AA=12. All four are 422 (unprocessable given the current subscription
   * state), validated BEFORE any write. The endpoint serves two paths — extend a
   * still-TRIALING subscription, or REVIVE a trial that already lapsed to the
   * `free` plan — so a rejection can come from either:
   *   - `NotTrialing` — the org has no subscription row, or its subscription is
   *     neither TRIALING nor on the `free` plan (it converted to a paid plan, is
   *     paused, …), so there is nothing to extend and nothing to revive.
   *   - `AlreadyExtended` — `trialExtendedAt` is set; the extension is one-time
   *     and survives a downgrade to `free`, so it blocks the revival path too.
   *     Deliberately distinct from `NotTrialing` so the UI can say "you've
   *     already used your extension" rather than a generic ineligibility.
   *   - `TrialNotExtendable` — incomplete data to extend from: a TRIALING
   *     subscription with no trial-end instant (`expiresAt` NULL) or no `planId`
   *     to record the audit transition against, or (on the revival path) no
   *     seeded `trial` plan / a `trial` plan with no `validityInDays`. An
   *     operator/data-integrity condition, not something the user can fix —
   *     hence the contact-support prose.
   *   - `ExtensionWindowClosed` — the revival path only: the org is on `free`
   *     with its extension unspent, but `organizations.createdAt` is older than
   *     `TRIAL_EXTENSION_ORG_AGE_DAYS`. Distinct from `NotTrialing` because the
   *     org WAS eligible and the window simply elapsed — the UI steers to
   *     upgrade rather than implying an error.
   */
  TrialExtension: {
    NotTrialing: '041201',
    AlreadyExtended: '041202',
    TrialNotExtendable: '041203',
    ExtensionWindowClosed: '041204',
  },
  /**
   * Configurable Discount Engine (P3, `DiscountService` + `/internal/discounts`).
   * AA=13. Staff-facing (internal-token guarded) discount CRUD + direct-apply.
   *   - `NotFound` — no discount for the id (404).
   *   - `Inactive` — the discount is disabled, not yet started, or expired (422).
   *   - `NotApplicable` — the discount's org/plan scope doesn't match the target (422).
   *   - `AutoApplyConflict` — creating an auto-apply discount whose plan scope
   *     overlaps an already-active auto-apply discount; checked BEFORE the Paddle
   *     call so at most one auto-apply resolves per plan (422).
   *   - `PaddleSyncFailed` — the upstream Paddle discounts/subscription call failed;
   *     no local row is written on a create failure (502).
   *   - `NoPaddleSubscription` — direct-apply/remove needs a live Paddle-backed
   *     subscription on the org (422).
   *   - `InvalidConfig` — cross-field config is invalid: `fixed` without a currency,
   *     `recurring` without `recurringIntervals`, or `organization` without a
   *     resolvable `organizationId` (422).
   *   - `NotRedeemableInApp` — a BROADCAST (`appliesTo: 'all'`) code was submitted
   *     on the in-app redeem path. Broadcast codes are applied at checkout, where
   *     Paddle's `usage_limit` counts them; the server-applied path has no such
   *     cap, so it's rejected here and steered to checkout (422).
   *   - `AlreadyRedeemed` — the org has already redeemed this code once. A code is
   *     redeemable by a given org at most once (the per-org redemption ledger) (422).
   *   - `DiscountAlreadyActive` — the subscription already carries a live discount.
   *     Paddle holds exactly ONE discount per subscription, so applying a second
   *     would silently clobber the first; the promo field re-opens when the current
   *     discount's period ends (422).
   *   - `UsageLimitReached` — the discount has hit its `maxRedemptions` cap. Paddle's
   *     own `usage_limit` only counts codes typed in the checkout overlay, so the
   *     server-applied paths (in-app redeem + self-serve upgrade) enforce the cap
   *     locally against the `discountRedemptions` ledger (422).
   *   - `OneOffNotApplicable` — a ONE-OFF (`duration: 'first_period'`) discount was
   *     submitted against an EXISTING subscription. `first_period` maps to Paddle
   *     `recur: false`, and Paddle rejects a non-recurring discount on
   *     `subscriptions.update` with `subscription_one_off_discount_not_valid`
   *     ("one-off discount invalid for subscription") regardless of `effectiveFrom`
   *     — verified against the sandbox API. Such a code is still perfectly valid at
   *     CHECKOUT, so this is a precondition on every path that attaches a discount
   *     to an EXISTING subscription (in-app redeem, staff direct-apply, AND the
   *     self-serve upgrade/preview), not a broken discount; caught locally on each
   *     so the customer gets a 422 instead of the 502 the Paddle rejection used to
   *     surface as `PaddleSyncFailed` on redeem and `Upgrade.UpgradeFailed` /
   *     `Upgrade.PreviewFailed` on upgrade (422).
   */
  Discount: {
    NotFound: '041301',
    Inactive: '041302',
    NotApplicable: '041303',
    AutoApplyConflict: '041304',
    PaddleSyncFailed: '041305',
    NoPaddleSubscription: '041306',
    InvalidConfig: '041307',
    NotRedeemableInApp: '041308',
    AlreadyRedeemed: '041309',
    DiscountAlreadyActive: '041310',
    UsageLimitReached: '041311',
    OneOffNotApplicable: '041312',
  },
} as const;

export const BILLING_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '040001': { httpStatus: 500, message: "We couldn't load your organization. Please try again." },
  '040002': {
    httpStatus: 500,
    message: "We couldn't load your plan details. Please try again.",
  },
  '040003': {
    httpStatus: 500,
    message: "We couldn't load your plan limits. Please try again.",
  },
  '040004': {
    httpStatus: 403,
    message: 'This feature is not available on your plan. Upgrade to unlock it.',
  },
  '040005': {
    httpStatus: 403,
    message: "You don't have access to this action on your current plan.",
  },
  '040006': {
    httpStatus: 403,
    message:
      'Your subscription is paused, so your account is view-only. Resume your subscription to make changes.',
  },
  '040007': {
    httpStatus: 403,
    message: 'Your plan is managed in Saleshandy. Make plan and billing changes from your Saleshandy account.',
  },
  '040008': {
    httpStatus: 403,
    message: 'Your plan was set up by our team, so you can only move to a higher plan. Contact support to move to a lower one.',
  },

  // ── Paddle webhook ──
  '040101': { httpStatus: 401, message: 'Webhook request could not be verified.' },
  '040102': { httpStatus: 401, message: 'Webhook request could not be verified.' },
  '040103': {
    httpStatus: 500,
    message: "We couldn't match your subscription to a plan. Please contact support.",
  },
  '040104': { httpStatus: 400, message: 'Webhook payload could not be parsed.' },
  '040105': {
    httpStatus: 500,
    message: "We couldn't match your subscription to a plan. Please contact support.",
  },

  // ── Customer portal ──
  '040501': {
    httpStatus: 400,
    message: "You don't have an active subscription to manage.",
  },
  '040502': {
    httpStatus: 502,
    message: "We couldn't open your billing portal right now. Please try again.",
  },

  // ── Checkout ──
  '040701': {
    httpStatus: 422,
    message: "This plan can't be purchased right now.",
  },

  // ── Custom plan ──
  '040801': {
    httpStatus: 422,
    message: "The parent plan you referenced doesn't exist.",
  },
  '040802': {
    httpStatus: 422,
    message:
      'This plan is missing one or more required limits, and the parent plan has no value to inherit. Set every limit explicitly.',
  },
  '040803': {
    httpStatus: 422,
    message:
      'This endpoint only edits custom plans. That is a base catalog plan - editing it would change the plan for every subscriber on it.',
  },
  '040804': {
    httpStatus: 422,
    message:
      "This plan's price can't be changed because it has active subscriptions. Create a new plan at the new price instead.",
  },

  '040805': {
    httpStatus: 422,
    message: 'A plan with this name already exists. Choose a different name.',
  },

  // ── Internal auth + manual assignment ──
  '040901': { httpStatus: 401, message: 'Invalid or missing internal API token.' },
  '040902': {
    httpStatus: 422,
    message:
      'This plan is billable in Paddle, so paddleCustomerId and paddleSubscriptionId are required to link the subscription.',
  },
  '040903': {
    httpStatus: 422,
    message:
      "That Paddle subscription is billing a different plan's price. Link a subscription that is on this plan's price.",
  },
  '040905': {
    httpStatus: 422,
    message:
      'Overrides cannot be set while changing the plan. Assign the plan first, then apply the overrides.',
  },
  '040904': {
    httpStatus: 502,
    message: 'Could not link the subscription in Paddle. Nothing was changed - try again.',
  },

  // ── Self-serve upgrade + proration preview (P2) ──
  '041001': {
    httpStatus: 422,
    message: 'You can only move to a higher plan from here. Downgrades are handled in the billing portal.',
  },
  '041002': {
    httpStatus: 422,
    message: "You don't have an active paid subscription to upgrade. Please subscribe first.",
  },
  '041003': {
    httpStatus: 422,
    message: 'Your subscription must be active before you can change plans.',
  },
  '041004': {
    httpStatus: 422,
    message: "You're already on this plan.",
  },
  '041005': {
    httpStatus: 402,
    message: "We couldn't process the payment for your upgrade. Please update your payment method in the billing portal.",
  },
  '041006': {
    httpStatus: 502,
    message: "We couldn't calculate your upgrade cost right now. Please try again.",
  },
  '041007': {
    httpStatus: 409,
    message: 'An upgrade is already in progress for your account. Please wait a moment and try again.',
  },
  '041008': {
    httpStatus: 502,
    message: "We couldn't complete your upgrade right now. Please try again.",
  },

  // ── Self-serve lifecycle (cancel / pause / resume) ──
  '041101': {
    httpStatus: 422,
    message: "You don't have an active subscription to change.",
  },
  '041102': {
    httpStatus: 422,
    message: 'Your subscription must be active to do that.',
  },
  '041103': {
    httpStatus: 422,
    message: 'Your subscription must be paused before you can resume it.',
  },
  '041104': {
    httpStatus: 502,
    message: "We couldn't update your subscription right now. Please try again.",
  },
  '041105': {
    httpStatus: 422,
    message:
      'A change is already scheduled for your subscription. Revoke it before scheduling another one.',
  },
  '041106': {
    httpStatus: 422,
    message: "You don't have a scheduled change to revoke.",
  },
  '041107': {
    httpStatus: 422,
    message:
      "We couldn't work out when your billing period ends, so we can't schedule a pause of that length. Pause until you resume instead, or contact support.",
  },

  // ── One-time trial extension ──
  '041201': {
    httpStatus: 422,
    message: "You don't have an active trial to extend.",
  },
  '041202': {
    httpStatus: 422,
    message: "You've already extended your trial. Trials can only be extended once.",
  },
  '041203': {
    httpStatus: 422,
    message: "We couldn't work out your trial end date. Please contact support.",
  },
  '041204': {
    httpStatus: 422,
    message:
      'The window for extending your trial has closed. Upgrade to keep warming up.',
  },

  // ── Configurable discounts (P3) ──
  '041301': {
    httpStatus: 404,
    message: "That discount doesn't exist.",
  },
  '041302': {
    httpStatus: 422,
    message: 'This discount is no longer active.',
  },
  '041303': {
    httpStatus: 422,
    message: "This discount can't be applied here.",
  },
  '041304': {
    httpStatus: 422,
    message: 'An active auto-apply discount already covers one or more of these plans.',
  },
  '041305': {
    httpStatus: 502,
    message: "We couldn't sync this discount with the payment provider. Please try again.",
  },
  '041306': {
    httpStatus: 422,
    message: 'This organization has no active paid subscription to apply a discount to.',
  },
  // The four rules this message lists are exactly the four `InvalidConfig`
  // throwers in DiscountService (validateConfig ×3 + resolveRestrictToPriceIds).
  // It previously claimed "a recurring discount needs a number of billing
  // periods" — a rule that does not exist and is in fact the opposite of the
  // truth: recurring WITHOUT intervals is a deliberate LIFETIME discount, so the
  // copy was talking staff into capping discounts they meant to run forever.
  '041307': {
    httpStatus: 422,
    message:
      'This discount configuration is invalid. A fixed discount needs a currency, a percentage discount needs a value above 0 and no more than 100, an organization discount needs a valid organization, and a plan-restricted discount needs at least one of those plans to have a checkout price.',
  },
  '041308': {
    httpStatus: 422,
    message: 'This code is applied at checkout, not here.',
  },
  '041309': {
    httpStatus: 422,
    message: "You've already used this code.",
  },
  '041310': {
    httpStatus: 422,
    message: 'You already have an active discount. You can apply a new code once it ends.',
  },
  '041311': {
    httpStatus: 422,
    message: 'That code has reached its redemption limit.',
  },
  '041312': {
    httpStatus: 422,
    message:
      "This code only discounts a first payment, so it can't be added to a subscription you already have. Use it at checkout instead.",
  },
};
